import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { 
  BarChart3, 
  Users, 
  Wrench, 
  AlertTriangle, 
  TrendingUp,
  TrendingDown,
  Send,
  Loader2,
  CheckCircle2,
  MessageSquare,
  Star,
  RefreshCw,
  Settings,
  Play,
  Phone,
  Trash2
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Score badge component
const ScoreBadge = ({ score }) => {
  if (!score) return <span className="text-slate-400">-</span>;
  
  const colors = {
    1: 'bg-red-100 text-red-700',
    2: 'bg-orange-100 text-orange-700',
    3: 'bg-green-100 text-green-700',
    4: 'bg-emerald-100 text-emerald-700'
  };
  
  const emojis = { 1: '😡', 2: '😕', 3: '🙂', 4: '😃' };
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-sm font-medium ${colors[score] || 'bg-slate-100'}`}>
      <span>{emojis[score]}</span>
      <span>{score.toFixed(1)}</span>
    </span>
  );
};

// Star display component
const StarDisplay = ({ rating }) => {
  if (!rating) return <span className="text-slate-400">-</span>;
  
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`h-4 w-4 ${
            star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-slate-200'
          }`}
        />
      ))}
      <span className="ml-1 text-sm text-slate-600">({rating.toFixed(1)})</span>
    </div>
  );
};

const CleanupResultView = ({ result }) => {
  const dryRun = result.dry_run;
  const checked = result.checked;
  const found = result.non_betaling_count;
  const deleted = result.deleted;
  const errors = result.errors;
  const orders = result.non_betaling_orders || [];
  const hasOrders = orders.length > 0;
  return (
    <>
      <p className="font-medium text-slate-900 mb-2">
        {dryRun ? 'Tør-kørsel resultat:' : 'Oprydning kørt:'}
      </p>
      <ul className="space-y-1 text-slate-700">
        <li>Tjekket: {checked} ordrer</li>
        <li>Ikke-Betaling fundet: <strong>{found}</strong></li>
        <li>Slettet: <strong className="text-red-600">{deleted}</strong></li>
        {errors > 0 && <li>Fejl ved opslag: {errors}</li>}
      </ul>
      {hasOrders && (
        <details className="mt-2">
          <summary className="cursor-pointer text-slate-600 text-xs">
            Vis de første {orders.length} ordrer
          </summary>
          <ul className="mt-2 text-xs space-y-0.5 font-mono">
            {orders.map((o) => {
              const oid = o.order_id;
              const sid = o.store_id;
              const cem = o.customer_email;
              const sent = o.email_sent_at ? 'Ja' : 'Nej';
              return (
                <li key={oid}>
                  #{oid} | store_id={sid} | {cem} | sendt: {sent}
                </li>
              );
            })}
          </ul>
        </details>
      )}
    </>
  );
};

const SatisfactionDashboard = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [sellers, setSellers] = useState([]);
  const [installers, setInstallers] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [alertLog, setAlertLog] = useState([]);
  const [settings, setSettings] = useState(null);
  const [period, setPeriod] = useState('30');
  
  // Test email state
  const [testEmail, setTestEmail] = useState('');
  const [testName, setTestName] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [testResult, setTestResult] = useState(null);
  
  // Followup modal
  const [followupSurvey, setFollowupSurvey] = useState(null);
  const [followupNote, setFollowupNote] = useState('');
  const [savingFollowup, setSavingFollowup] = useState(false);
  
  // Trigger sync state
  const [triggering, setTriggering] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  
  // Include test data toggle
  const [includeTest, setIncludeTest] = useState(true);
  
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    loadData();
  }, [period, includeTest]);

  const loadData = async () => {
    setLoading(true);
    const testParam = includeTest ? '&include_test=true' : '';
    try {
      const [dashRes, sellersRes, installersRes, alertsRes, settingsRes, alertLogRes] = await Promise.all([
        axios.get(`${API}/admin/satisfaction/dashboard?days=${period}${testParam}`, { headers }),
        axios.get(`${API}/admin/satisfaction/by-seller?days=${period}${testParam}`, { headers }),
        axios.get(`${API}/admin/satisfaction/by-installer?days=${period}${testParam}`, { headers }),
        axios.get(`${API}/admin/satisfaction/alerts?days=${period}${testParam}`, { headers }),
        axios.get(`${API}/admin/satisfaction/settings`, { headers }),
        axios.get(`${API}/admin/satisfaction/alert-log?limit=10`, { headers })
      ]);
      
      setDashboard(dashRes.data);
      setSellers(sellersRes.data.sellers || []);
      setInstallers(installersRes.data.installers || []);
      setAlerts(alertsRes.data.alerts || []);
      setSettings(settingsRes.data);
      setAlertLog(alertLogRes.data.alerts || []);
      setTestEmail(settingsRes.data?.test_email || '');
    } catch (err) {
      console.error('Failed to load satisfaction data:', err);
    } finally {
      setLoading(false);
    }
  };

  const updateSettings = async (newSettings) => {
    try {
      await axios.post(`${API}/admin/satisfaction/settings`, newSettings, { headers });
      setSettings(newSettings);
    } catch (err) {
      console.error('Failed to update settings:', err);
    }
  };

  const sendTestEmail = async () => {
    if (!testEmail) return;
    setSendingTest(true);
    setTestResult(null);
    
    try {
      const res = await axios.post(`${API}/admin/satisfaction/test`, {
        email: testEmail,
        name: testName || 'Test Kunde'
      }, { headers });
      setTestResult({ success: true, message: res.data.message, url: res.data.feedback_url });
    } catch (err) {
      setTestResult({ success: false, message: err.response?.data?.detail || 'Fejl ved afsendelse' });
    } finally {
      setSendingTest(false);
    }
  };

  const triggerSync = async () => {
    setTriggering(true);
    setSyncResult(null);
    
    try {
      const res = await axios.post(`${API}/admin/satisfaction/trigger-sync`, {}, { headers });
      const initialResult = res.data.result;
      
      if (initialResult.status === 'started' || initialResult.status === 'already_running') {
        // Poll for completion
        setSyncResult({ status: 'running' });
        const pollInterval = setInterval(async () => {
          try {
            const statusRes = await axios.get(`${API}/admin/satisfaction/sync-status`, { headers });
            if (!statusRes.data.running) {
              clearInterval(pollInterval);
              setSyncResult(statusRes.data.result);
              setTriggering(false);
              loadData();
            }
          } catch {
            clearInterval(pollInterval);
            setTriggering(false);
          }
        }, 3000);
      } else {
        // Immediate result (disabled, test_mode_no_email, etc.)
        setSyncResult(initialResult);
        setTriggering(false);
      }
    } catch (err) {
      setSyncResult({ status: 'error', error: err.response?.data?.detail || 'Sync fejlede' });
      setTriggering(false);
    }
  };

  const handleFollowup = async () => {
    if (!followupSurvey) return;
    setSavingFollowup(true);
    
    try {
      await axios.post(`${API}/admin/satisfaction/alerts/${followupSurvey.id}/followup`, {
        note: followupNote
      }, { headers });
      
      // Update local state - keep in list but mark as followed up
      setAlerts(alerts.map(a =>
        a.id === followupSurvey.id
          ? { ...a, followed_up_at: new Date().toISOString(), followup_note: followupNote }
          : a
      ));
      setFollowupSurvey(null);
      setFollowupNote('');
    } catch (err) {
      console.error('Failed to mark followup:', err);
    } finally {
      setSavingFollowup(false);
    }
  };

  const [cleaningUp, setCleaningUp] = useState(false);
  const [cleanupResult, setCleanupResult] = useState(null);
  const [deletingAlertId, setDeletingAlertId] = useState(null);
  const [resettingAlertId, setResettingAlertId] = useState(null);

  const deleteAlert = async (alert) => {
    if (!window.confirm(`Slet anmeldelse fra ${alert.customer_name} (#${alert.order_id}) permanent?`)) {
      return;
    }
    setDeletingAlertId(alert.id);
    try {
      await axios.delete(`${API}/admin/satisfaction/alerts/${alert.id}`, { headers });
      setAlerts(prev => prev.filter(a => a.id !== alert.id));
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Kunne ikke slette anmeldelsen';
      window.alert(detail);
    } finally {
      setDeletingAlertId(null);
    }
  };
  
  const resetAlert = async (alert) => {
    if (!window.confirm(
      `Nulstil falsk vurdering fra ${alert.customer_name} (#${alert.order_id})?\n\n` +
      `Bruges når en email-scanner eller bot har klikket på et rating-link. ` +
      `Vurderingen fjernes så kunden kan vurdere selv.`
    )) {
      return;
    }
    setResettingAlertId(alert.id);
    try {
      await axios.post(`${API}/admin/satisfaction/alerts/${alert.id}/reset`, {}, { headers });
      setAlerts(prev => prev.filter(a => a.id !== alert.id));
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Kunne ikke nulstille vurderingen';
      window.alert(detail);
    } finally {
      setResettingAlertId(null);
    }
  };

  const runCleanup = async (dryRun) => {
    setCleaningUp(true);
    setCleanupResult(null);
    try {
      const res = await axios.post(
        `${API}/admin/satisfaction/cleanup-non-betaling?dry_run=${dryRun}`,
        {},
        { headers, timeout: 180000 }
      );
      setCleanupResult(res.data);
      if (!dryRun) {
        // Refresh alert log and alerts after cleanup
        loadData();
      }
    } catch (err) {
      setCleanupResult({ error: err.response?.data?.detail || err.message });
    } finally {
      setCleaningUp(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <Skeleton key={i} className="h-32" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Kundetilfredshed</h1>
          <p className="text-slate-500">Feedback fra kunder efter levering</p>
        </div>
        
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 mr-2">
            <Switch
              id="include-test"
              checked={includeTest}
              onCheckedChange={setIncludeTest}
            />
            <Label htmlFor="include-test" className="text-sm text-slate-600 cursor-pointer">
              Vis test-data
            </Label>
          </div>
          
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Sidste 7 dage</SelectItem>
              <SelectItem value="30">Sidste 30 dage</SelectItem>
              <SelectItem value="90">Sidste 90 dage</SelectItem>
              <SelectItem value="365">Sidste år</SelectItem>
            </SelectContent>
          </Select>
          
          <Button variant="outline" size="icon" onClick={loadData}>
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList>
          <TabsTrigger value="overview" className="gap-2">
            <BarChart3 className="h-4 w-4" />
            Overblik
          </TabsTrigger>
          <TabsTrigger value="sellers" className="gap-2">
            <Users className="h-4 w-4" />
            Sælgere
          </TabsTrigger>
          <TabsTrigger value="installers" className="gap-2">
            <Wrench className="h-4 w-4" />
            Montører
          </TabsTrigger>
          <TabsTrigger value="alerts" className="gap-2">
            <AlertTriangle className="h-4 w-4" />
            Alerts
            {alerts.filter(a => !a.followed_up_at).length > 0 && (
              <Badge variant="destructive" className="ml-1">
                {alerts.filter(a => !a.followed_up_at).length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="settings" className="gap-2">
            <Settings className="h-4 w-4" />
            Indstillinger
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          {/* KPI Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Gns. tilfredshed</p>
                    <p className="text-3xl font-bold text-slate-900">
                      {dashboard?.avg_score?.toFixed(1) || '-'}
                    </p>
                  </div>
                  <div className="text-4xl">
                    {dashboard?.avg_score >= 3.5 ? '😃' : 
                     dashboard?.avg_score >= 2.5 ? '🙂' :
                     dashboard?.avg_score >= 1.5 ? '😕' : '😡'}
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Svarprocent</p>
                    <p className="text-3xl font-bold text-slate-900">
                      {dashboard?.response_rate?.toFixed(0) || 0}%
                    </p>
                  </div>
                  <div className="text-right text-sm text-slate-500">
                    <p>{dashboard?.total_responses || 0} svar</p>
                    <p>af {dashboard?.total_sent || 0} sendt</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Utilfredse</p>
                    <p className="text-3xl font-bold text-red-600">
                      {dashboard?.dissatisfied_pct?.toFixed(0) || 0}%
                    </p>
                  </div>
                  <div className="text-right text-sm text-slate-500">
                    <p>{dashboard?.dissatisfied_count || 0} kunder</p>
                    <p>score ≤ 2</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-slate-500 mb-3">Score fordeling</p>
                <div className="space-y-2">
                  {['meget_tilfreds', 'tilfreds', 'utilfreds', 'meget_utilfreds'].map((key, i) => {
                    const labels = ['😃 Meget tilfreds', '🙂 Tilfreds', '😕 Utilfreds', '😡 Meget utilfreds'];
                    const colors = ['bg-emerald-500', 'bg-green-500', 'bg-orange-500', 'bg-red-500'];
                    const count = dashboard?.score_distribution?.[key] || 0;
                    const total = dashboard?.total_responses || 1;
                    const pct = (count / total * 100) || 0;
                    
                    return (
                      <div key={key} className="flex items-center gap-2">
                        <span className="text-xs w-24 truncate">{labels[i]}</span>
                        <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div 
                            className={`h-full ${colors[i]}`}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <span className="text-xs text-slate-500 w-8">{count}</span>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Seller & Installer averages */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Gns. sælger rating</CardTitle>
              </CardHeader>
              <CardContent>
                <StarDisplay rating={dashboard?.avg_seller_rating} />
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Gns. montage rating</CardTitle>
              </CardHeader>
              <CardContent>
                <StarDisplay rating={dashboard?.avg_installer_rating} />
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Sellers Tab */}
        <TabsContent value="sellers" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Performance pr. sælger</CardTitle>
              <CardDescription>Baseret på kundefeedback de sidste {period} dage</CardDescription>
            </CardHeader>
            <CardContent>
              {sellers.length === 0 ? (
                <p className="text-slate-500 text-center py-8">Ingen data endnu</p>
              ) : (
                <div className="space-y-4">
                  {sellers.map((seller) => (
                    <div key={seller._id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                      <div className="flex-1">
                        <p className="font-medium text-slate-900">{seller.seller_name || 'Ukendt'}</p>
                        <p className="text-sm text-slate-500">{seller.total_responses} besvarelser</p>
                      </div>
                      <div className="flex items-center gap-6">
                        <div className="text-center">
                          <p className="text-xs text-slate-500">Score</p>
                          <ScoreBadge score={seller.avg_score} />
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-slate-500">Sælger</p>
                          <StarDisplay rating={seller.avg_seller_rating} />
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-slate-500">Utilfredse</p>
                          <span className={`text-sm font-medium ${seller.dissatisfied_pct > 20 ? 'text-red-600' : 'text-slate-600'}`}>
                            {seller.dissatisfied_pct}%
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Installers Tab */}
        <TabsContent value="installers" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Performance pr. montør</CardTitle>
              <CardDescription>Baseret på kundefeedback de sidste {period} dage</CardDescription>
            </CardHeader>
            <CardContent>
              {installers.length === 0 ? (
                <p className="text-slate-500 text-center py-8">Ingen data endnu</p>
              ) : (
                <div className="space-y-4">
                  {installers.map((installer) => (
                    <div key={installer._id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                      <div className="flex-1">
                        <p className="font-medium text-slate-900">{installer.installer_name || 'Ukendt'}</p>
                        <p className="text-sm text-slate-500">{installer.total_responses} besvarelser</p>
                      </div>
                      <div className="flex items-center gap-6">
                        <div className="text-center">
                          <p className="text-xs text-slate-500">Score</p>
                          <ScoreBadge score={installer.avg_score} />
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-slate-500">Montage</p>
                          <StarDisplay rating={installer.avg_installer_rating} />
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-slate-500">Utilfredse</p>
                          <span className={`text-sm font-medium ${installer.dissatisfied_pct > 20 ? 'text-red-600' : 'text-slate-600'}`}>
                            {installer.dissatisfied_pct}%
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Alerts Tab */}
        <TabsContent value="alerts" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-red-500" />
                Dårlige anmeldelser (score ≤ 2)
              </CardTitle>
              <CardDescription>
                Alle anmeldelser med score 1-2 fra de sidste {period} dage. "Fulgt op"-badge vises på dem der allerede er behandlet.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {alerts.length === 0 ? (
                <div className="text-center py-8">
                  <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-2" />
                  <p className="text-slate-500">Ingen dårlige anmeldelser i perioden - godt gået!</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {alerts.map((alert) => {
                    const isFollowedUp = !!alert.followed_up_at;
                    const rowBg = isFollowedUp ? 'bg-slate-50 border-slate-200' : 'bg-red-50 border-red-100';
                    return (
                      <div key={alert.id} className={`flex items-start justify-between p-4 border rounded-lg ${rowBg}`} data-testid={`alert-row-${alert.id}`}>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <span className="text-2xl">
                              {alert.overall_score === 1 ? '😡' : '😕'}
                            </span>
                            <span className="font-medium text-slate-900">{alert.customer_name}</span>
                            <Badge variant="outline" className="text-xs">
                              Ordre #{alert.order_id}
                            </Badge>
                            {alert.is_test && (
                              <Badge variant="secondary" className="text-xs">Test</Badge>
                            )}
                            {alert.wants_contact && (
                              <Badge
                                className="text-xs bg-red-600 hover:bg-red-700 text-white gap-1"
                                data-testid={`wants-contact-badge-${alert.id}`}
                              >
                                <Phone className="h-3 w-3" />
                                Ønsker kontakt
                              </Badge>
                            )}
                            {isFollowedUp && (
                              <Badge className="text-xs bg-green-600 hover:bg-green-700 text-white gap-1">
                                <CheckCircle2 className="h-3 w-3" />
                                Fulgt op
                              </Badge>
                            )}
                          </div>
                          {alert.wants_contact && (
                            <p className="text-xs text-red-700 font-medium mt-1">
                              Ring til {alert.customer_phone || 'kunden'} eller mail {alert.customer_email || ''}
                            </p>
                          )}
                          {alert.comment && (
                            <p className="text-sm text-slate-600 mt-2 flex items-start gap-2">
                              <MessageSquare className="h-4 w-4 mt-0.5 flex-shrink-0" />
                              "{alert.comment}"
                            </p>
                          )}
                          <p className="text-xs text-slate-400 mt-2">
                            {alert.rated_at
                              ? new Date(alert.rated_at).toLocaleString('da-DK', {
                                  day: '2-digit', month: '2-digit', year: 'numeric',
                                  hour: '2-digit', minute: '2-digit'
                                })
                              : 'Ikke bedømt endnu'}
                            {' '}· Sælger: {alert.seller_name || '-'} | Montør: {alert.installer_name || '-'}
                          </p>
                          {isFollowedUp && alert.followup_note && (
                            <p className="text-xs text-slate-500 mt-1 italic">
                              Note: "{alert.followup_note}"
                            </p>
                          )}
                        </div>
                        {!isFollowedUp ? (
                          <div className="flex flex-col gap-2 items-end">
                            <Button
                              size="sm"
                              onClick={() => setFollowupSurvey(alert)}
                            >
                              Markér som fulgt op
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-slate-500 hover:text-slate-700 text-xs"
                              onClick={() => resetAlert(alert)}
                              disabled={resettingAlertId === alert.id}
                              data-testid={`reset-alert-btn-${alert.id}`}
                              title="Bruges hvis email-scanner har klikket ved en fejl"
                            >
                              {resettingAlertId === alert.id ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                'Falsk klik? Nulstil'
                              )}
                            </Button>
                          </div>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                            onClick={() => deleteAlert(alert)}
                            disabled={deletingAlertId === alert.id}
                            data-testid={`delete-alert-btn-${alert.id}`}
                          >
                            {deletingAlertId === alert.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <>
                                <Trash2 className="h-4 w-4 mr-1" />
                                Slet
                              </>
                            )}
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings" className="space-y-6">
          {/* Enable/Test Mode */}
          <Card>
            <CardHeader>
              <CardTitle>Aktivering</CardTitle>
              <CardDescription>Kontrollér om tilfredshedsundersøgelser sendes automatisk</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Aktivér automatisk afsendelse</p>
                  <p className="text-sm text-slate-500">Sender emails til kunder når ordrer bliver "Complete"</p>
                </div>
                <Switch
                  checked={settings?.enabled || false}
                  onCheckedChange={(checked) => updateSettings({ ...settings, enabled: checked })}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Test-tilstand</p>
                  <p className="text-sm text-slate-500">Sender alle emails til test-adressen i stedet for kunder</p>
                </div>
                <Switch
                  checked={settings?.test_mode ?? true}
                  onCheckedChange={(checked) => updateSettings({ ...settings, test_mode: checked })}
                />
              </div>
              
              {settings?.test_mode && (
                <div className="space-y-2">
                  <Label>Test email-adresse</Label>
                  <Input
                    value={settings?.test_email || ''}
                    onChange={(e) => updateSettings({ ...settings, test_email: e.target.value })}
                    placeholder="din@email.dk"
                  />
                </div>
              )}
              
              <div className="space-y-2">
                <Label>Kig tilbage (dage)</Label>
                <Input
                  type="number"
                  value={settings?.lookback_days || 14}
                  onChange={(e) => updateSettings({ ...settings, lookback_days: parseInt(e.target.value) })}
                  min={1}
                  max={60}
                />
                <p className="text-xs text-slate-500">Hvor langt tilbage i tid der skal hentes ordrer</p>
              </div>
              
              <div>
                <Label>Max emails pr. kørsel</Label>
                <Input
                  type="number"
                  value={settings?.max_emails_per_run || 30}
                  onChange={(e) => updateSettings({ ...settings, max_emails_per_run: parseInt(e.target.value) })}
                  min={5}
                  max={200}
                />
                <p className="text-xs text-slate-500">Begræns antal emails pr. sync for at undgå overbelastning</p>
              </div>
              
              <div>
                <Label>Alert-emails (salgschefer)</Label>
                <Input
                  value={settings?.alert_emails || ''}
                  onChange={(e) => updateSettings({ ...settings, alert_emails: e.target.value })}
                  placeholder="mi@gardinbussen.org, cl@gardinbussen.org"
                  data-testid="alert-emails-input"
                />
                <p className="text-xs text-slate-500">Kommaseparerede emails der modtager besked ved score 1-2 (Utilfreds/Meget utilfreds)</p>
              </div>
              
              <div>
                <Label>Alert-telefoner (SMS til salgschefer)</Label>
                <Input
                  value={settings?.alert_phones || ''}
                  onChange={(e) => updateSettings({ ...settings, alert_phones: e.target.value })}
                  placeholder="+4512345678, +4587654321"
                  data-testid="alert-phones-input"
                />
                <p className="text-xs text-slate-500">Kommaseparerede telefonnumre der modtager SMS sideløbende med email ved score 1-2. Brug E.164 format (+45...)</p>
              </div>
            </CardContent>
          </Card>

          {/* Alert delivery log */}
          <Card data-testid="alert-log-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-slate-600" />
                Alert-historik (seneste 10)
              </CardTitle>
              <CardDescription>Viser leveringsstatus for de seneste alerts (score 1-2)</CardDescription>
            </CardHeader>
            <CardContent>
              {alertLog.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-4">Ingen alerts endnu</p>
              ) : (
                <div className="space-y-2">
                  {alertLog.map((entry) => {
                    const emailOk = !!(entry.alert_sent_at || entry.extended_alert_sent_at);
                    const smsOk = !!(entry.alert_sms_sent_at || entry.extended_alert_sms_sent_at);
                    const ratedDate = entry.rated_at ? new Date(entry.rated_at).toLocaleString('da-DK', {
                      day: '2-digit', month: '2-digit', year: '2-digit',
                      hour: '2-digit', minute: '2-digit'
                    }) : '-';
                    return (
                      <div
                        key={entry.id}
                        className="flex items-center justify-between gap-3 p-3 border border-slate-200 rounded-lg text-sm"
                        data-testid={`alert-log-row-${entry.id}`}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span>{entry.overall_score === 1 ? '😡' : '😕'}</span>
                            <span className="font-medium truncate">{entry.customer_name || 'Ukendt'}</span>
                            <Badge variant="outline" className="text-xs">#{entry.order_id}</Badge>
                            {entry.is_test && (
                              <Badge variant="secondary" className="text-xs">Test</Badge>
                            )}
                            {entry.wants_contact && (
                              <Badge className="text-xs bg-red-600 hover:bg-red-700 text-white gap-1">
                                <Phone className="h-3 w-3" />
                                Kontakt
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-slate-400 mt-1">{ratedDate}</p>
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0">
                          <div className="flex items-center gap-1 text-xs" title={emailOk ? `Email sendt ${entry.alert_sent_at || entry.extended_alert_sent_at}` : 'Email ikke sendt'}>
                            <span className="font-medium text-slate-600">Email</span>
                            {emailOk ? (
                              <CheckCircle2 className="h-4 w-4 text-green-600" />
                            ) : (
                              <span className="h-4 w-4 inline-flex items-center justify-center rounded-full bg-slate-200 text-slate-500 text-[10px] font-bold">×</span>
                            )}
                          </div>
                          <div className="flex items-center gap-1 text-xs" title={smsOk ? `SMS sendt til ${entry.alert_sms_sent_count || entry.extended_alert_sms_sent_count || 0} modtagere` : 'SMS ikke sendt'}>
                            <span className="font-medium text-slate-600">SMS</span>
                            {smsOk ? (
                              <CheckCircle2 className="h-4 w-4 text-green-600" />
                            ) : (
                              <span className="h-4 w-4 inline-flex items-center justify-center rounded-full bg-slate-200 text-slate-500 text-[10px] font-bold">×</span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Cleanup Non-Betaling Surveys */}
          <Card data-testid="cleanup-non-betaling-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-600" />
                Ryd op i ikke-Betaling surveys
              </CardTitle>
              <CardDescription>
                Scan alle eksisterende surveys i databasen og slet dem der tilhører Gardinshoppen (store_id≠5).
                Bruges som engangs-oprydning efter det gamle filter slap fx ordre #1042168 igennem.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex gap-2 flex-wrap">
                <Button
                  variant="outline"
                  onClick={() => runCleanup(true)}
                  disabled={cleaningUp}
                  data-testid="cleanup-dry-run-btn"
                >
                  {cleaningUp ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Tør-kørsel (vis hvad der ville blive slettet)
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => {
                    if (window.confirm('Er du sikker? Dette sletter permanent alle survey-records for ordrer der ikke er fra Betaling (store_id=5).')) {
                      runCleanup(false);
                    }
                  }}
                  disabled={cleaningUp}
                  data-testid="cleanup-run-btn"
                >
                  {cleaningUp ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Kør oprydning
                </Button>
              </div>
              {cleanupResult && (
                <div className="mt-3 p-3 bg-slate-50 border border-slate-200 rounded-lg text-sm">
                  {cleanupResult.error ? (
                    <p className="text-red-600">Fejl: {cleanupResult.error}</p>
                  ) : (
                    <CleanupResultView result={cleanupResult} />
                  )}
                </div>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>Manuel sync</CardTitle>
              <CardDescription>Kør sync-job manuelt for at hente nye "Complete" ordrer</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Button
                onClick={triggerSync}
                disabled={triggering}
                className="w-full"
              >
                {triggering ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Play className="h-4 w-4 mr-2" />
                )}
                Kør sync nu
              </Button>
              
              {syncResult && (
                <div className={`p-4 rounded-lg ${
                  syncResult.status === 'error' ? 'bg-red-50' : 
                  syncResult.status === 'disabled' ? 'bg-yellow-50' : 
                  syncResult.status === 'test_mode_no_email' ? 'bg-orange-50' : 
                  syncResult.status === 'running' ? 'bg-blue-50' :
                  'bg-green-50'}`}>
                  <p className="font-medium">
                    {syncResult.status === 'error' ? 'Fejl' : 
                     syncResult.status === 'disabled' ? 'Deaktiveret - aktiver først under Indstillinger' :
                     syncResult.status === 'test_mode_no_email' ? 'Test-tilstand aktiv men ingen test-email angivet' :
                     syncResult.status === 'running' ? 'Sync kører i baggrunden...' :
                     'Sync gennemført'}
                  </p>
                  {syncResult.status === 'running' && (
                    <p className="text-sm text-blue-600 mt-1">Henter ordrer og sender emails. Dette kan tage 1-2 minutter.</p>
                  )}
                  {syncResult.orders_found !== undefined && (
                    <p className="text-sm text-slate-600">
                      Fandt {syncResult.orders_found} ordrer, sendte {syncResult.emails_sent} emails
                      {syncResult.skipped > 0 && `, sprunget over ${syncResult.skipped} (allerede sendt)`}
                    </p>
                  )}
                  {syncResult.batch_limited && (
                    <p className="text-sm text-amber-600 mt-1">
                      Batch-grænse nået. {syncResult.remaining} ordrer afventer næste kørsel.
                    </p>
                  )}
                  {syncResult.sent_to && syncResult.emails_sent > 0 && (
                    <p className="text-sm text-blue-600 font-medium mt-1">
                      Sendt til: {syncResult.sent_to}
                    </p>
                  )}
                  {syncResult.test_mode && syncResult.status === 'success' && (
                    <p className="text-xs text-amber-600 mt-1">Test-tilstand aktiv</p>
                  )}
                  {syncResult.error && (
                    <p className="text-sm text-red-600">{syncResult.error}</p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Google Sheets Sync */}
          <Card>
            <CardHeader>
              <CardTitle>Google Sheets</CardTitle>
              <CardDescription>Synkroniser data automatisk til et Google Sheet</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Google Sheet ID</Label>
                <Input
                  value={settings?.google_sheet_id || ''}
                  onChange={(e) => updateSettings({ ...settings, google_sheet_id: e.target.value })}
                  placeholder="1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgVE2upms"
                  data-testid="sheet-id-input"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Del arket med: <span className="font-mono text-xs select-all">gardinbussen-drive-uploader@intranet-488010.iam.gserviceaccount.com</span>
                </p>
              </div>
              
              <Button
                data-testid="sync-sheet-btn"
                onClick={async () => {
                  try {
                    const res = await axios.post(`${API}/admin/satisfaction/sync-sheet?include_test=${includeTest}`, {}, { headers });
                    alert(`Synkroniseret ${res.data.rows_synced} rækker til Google Sheet`);
                  } catch (err) {
                    alert(err.response?.data?.detail || 'Sheet sync fejlede');
                  }
                }}
                disabled={!settings?.google_sheet_id}
                variant="outline"
                className="w-full"
              >
                Synkroniser nu
              </Button>
              
              <Button
                data-testid="migrate-roles-btn"
                onClick={async () => {
                  try {
                    const res = await axios.post(`${API}/admin/satisfaction/migrate-roles`, {}, { headers });
                    alert(`Opdateret ${res.data.updated} af ${res.data.total_surveys} surveys${res.data.sheet_synced ? ' + Google Sheet opdateret' : ''}`);
                    loadData();
                  } catch (err) {
                    alert(err.response?.data?.detail || 'Migrering fejlede');
                  }
                }}
                variant="outline"
                className="w-full"
              >
                Opdater sælger/montør-roller
              </Button>
              
              {settings?.google_sheet_id && (
                <p className="text-xs text-green-600">Auto-sync aktiv: data opdateres automatisk ved nye besvarelser</p>
              )}
            </CardContent>
          </Card>

          {/* Test Email */}
          <Card>
            <CardHeader>
              <CardTitle>Send test-email</CardTitle>
              <CardDescription>Send en test-email for at se hvordan den ser ud</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                    placeholder="test@example.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Kundenavn</Label>
                  <Input
                    value={testName}
                    onChange={(e) => setTestName(e.target.value)}
                    placeholder="Test Kunde"
                  />
                </div>
              </div>
              
              <Button
                onClick={sendTestEmail}
                disabled={sendingTest || !testEmail}
                className="w-full"
              >
                {sendingTest ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Send className="h-4 w-4 mr-2" />
                )}
                Send test-email
              </Button>
              
              {testResult && (
                <div className={`p-4 rounded-lg ${testResult.success ? 'bg-green-50' : 'bg-red-50'}`}>
                  <p className={testResult.success ? 'text-green-700' : 'text-red-700'}>
                    {testResult.message}
                  </p>
                  {testResult.url && (
                    <a 
                      href={testResult.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:underline"
                    >
                      Åbn feedback-side
                    </a>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Followup Dialog */}
      <Dialog open={!!followupSurvey} onOpenChange={() => setFollowupSurvey(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Markér som fulgt op</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-slate-600">
              Kunde: <span className="font-medium">{followupSurvey?.customer_name}</span><br />
              Ordre: <span className="font-medium">#{followupSurvey?.order_id}</span>
            </p>
            <div className="space-y-2">
              <Label>Note (valgfrit)</Label>
              <Textarea
                value={followupNote}
                onChange={(e) => setFollowupNote(e.target.value)}
                placeholder="Hvad blev der gjort..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFollowupSurvey(null)}>
              Annullér
            </Button>
            <Button onClick={handleFollowup} disabled={savingFollowup}>
              {savingFollowup ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Gem'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SatisfactionDashboard;
